// Quick-action modal for advancing a milestone through its lifecycle.
// Thin wrapper around the shared LifecycleTransitionDialog: translates
// the milestone derived status into the forward-only option set and
// maps the picked option into a MilestoneProgressPatch ready for the
// existing onProgress callback.
//
// External API to MilestoneTab is unchanged — props, behaviour, and
// the data-testid surface stay bit-identical so the regression
// contract with ProgressMilestoneModal.test.tsx holds.
//
// Available transitions (forward only):
//   upcoming           → delivered
//   delivered          → partially-accepted, accepted
//   partially-accepted → accepted (full), paid (carve-out)
//   accepted           → paid
//   paid (against partial, no full acceptance) → accepted (full)
//                        — the carve-out leaves full acceptance outstanding,
//                          so it stays offerable even after payment
//   paid (fully accepted) → (terminal; modal renders the "already paid" copy)

import LifecycleTransitionDialog, {
  type Outcome,
} from "../shared/LifecycleTransitionDialog";
import {
  deriveMilestoneLifecycleStatus,
  type MilestoneLifecycleTimestamps,
} from "../../../../utils/deriveMilestoneStatus";
import { isSet } from "../../../../utils/milestoneInput";
import { getBrisbaneToday } from "../../../../utils/getBrisbaneToday";
import type { Milestone } from "../../../../types";

type LifecycleField =
  | "deliveredAt"
  | "partiallyAcceptedAt"
  | "acceptedAt"
  | "paidAt";

type LifecycleCommentField =
  | "deliveredComment"
  | "partiallyAcceptedComment"
  | "acceptedComment"
  | "paidComment";

const COMMENT_FIELD: Record<LifecycleField, LifecycleCommentField> = {
  deliveredAt: "deliveredComment",
  partiallyAcceptedAt: "partiallyAcceptedComment",
  acceptedAt: "acceptedComment",
  paidAt: "paidComment",
};

/** Patch shape sent on progress: the picked timestamp plus an optional
 *  free-text comment for that stage. The comment field is omitted entirely
 *  when the user leaves the textarea empty. */
export type MilestoneProgressPatch = Partial<MilestoneLifecycleTimestamps> &
  Partial<Record<LifecycleCommentField, string>>;

interface MilestoneOptionShape {
  field: LifecycleField;
  label: string;
  minDate?: string;
}

function nextOptions(m: Milestone): MilestoneOptionShape[] {
  const status = deriveMilestoneLifecycleStatus(m);
  switch (status) {
    case "upcoming":
      return [{ field: "deliveredAt", label: "Mark as delivered" }];
    case "delivered":
      return [
        {
          field: "partiallyAcceptedAt",
          label: "Mark as partially accepted",
          minDate: m.deliveredAt ?? undefined,
        },
        {
          field: "acceptedAt",
          label: "Mark as accepted",
          minDate: m.deliveredAt ?? undefined,
        },
      ];
    case "partially-accepted":
      return [
        {
          field: "acceptedAt",
          label: "Mark as accepted (full)",
          minDate: m.partiallyAcceptedAt ?? m.deliveredAt ?? undefined,
        },
        {
          field: "paidAt",
          label: "Mark as paid",
          minDate: m.partiallyAcceptedAt ?? undefined,
        },
      ];
    case "accepted":
      return [
        {
          field: "paidAt",
          label: "Mark as paid",
          minDate: m.acceptedAt ?? undefined,
        },
      ];
    case "paid":
      // Paid-against-partial carve-out: a milestone can be paid while only
      // partially accepted, so full acceptance is still an outstanding
      // forward step even though payment is recorded. Offer it (and only it —
      // payment is already done). The full-acceptance date may fall after the
      // payment date; minDate is the partial-acceptance rung.
      if (isSet(m.partiallyAcceptedAt) && !isSet(m.acceptedAt)) {
        return [
          {
            field: "acceptedAt",
            label: "Mark as accepted (full)",
            minDate: m.partiallyAcceptedAt ?? m.deliveredAt ?? undefined,
          },
        ];
      }
      return [];
  }
}

// Pill register per FORWARD field — the chip on the outcome row reads
// as the state the action transitions TO.
const FIELD_PILL: Record<LifecycleField, {
  variant: "draft" | "active" | "outlinedAmber";
  label: string;
}> = {
  deliveredAt: { variant: "draft", label: "Delivered" },
  partiallyAcceptedAt: { variant: "outlinedAmber", label: "Partially accepted" },
  acceptedAt: { variant: "active", label: "Accepted" },
  paidAt: { variant: "active", label: "Paid" },
};

const FIELD_DESCRIPTION: Record<LifecycleField, string> = {
  deliveredAt: "Record the delivery date.",
  partiallyAcceptedAt:
    "Record a partial acceptance — outstanding items still need to clear.",
  acceptedAt: "Full acceptance.",
  paidAt: "Record the payment date.",
};

function toOutcomes(options: MilestoneOptionShape[]): Outcome[] {
  return options.map((opt) => ({
    id: opt.field,
    label: opt.label,
    pill: FIELD_PILL[opt.field].variant,
    pillLabel: FIELD_PILL[opt.field].label,
    description: FIELD_DESCRIPTION[opt.field],
    requiresComment: false,
    targetState: opt.field,
  }));
}

interface ProgressMilestoneModalProps {
  isOpen: boolean;
  milestone: Milestone | null;
  onClose: () => void;
  onProgress: (
    milestone: Milestone,
    update: MilestoneProgressPatch,
  ) => Promise<unknown>;
}

const TEST_IDS = {
  modal: "progress-milestone-modal",
  outcomePrefix: "progress-option",
  save: "progress-save",
  dateInput: "progress-date-input",
  commentInput: "progress-comment-input",
};

export default function ProgressMilestoneModal({
  isOpen,
  milestone,
  onClose,
  onProgress,
}: ProgressMilestoneModalProps) {
  if (!milestone) return null;

  const options = nextOptions(milestone);
  const outcomes = toOutcomes(options);
  // All options for a given resting state share the same minDate (the
  // upstream stage's timestamp), so the shared dialog's single-minDate
  // API is a faithful representation of milestone behaviour today. The
  // carve-out test ("partial → paid uses partiallyAcceptedAt as min,
  // not deliveredAt") relies on this: from partially-accepted, both
  // options use partiallyAcceptedAt as their min.
  const minDate = options.find((o) => o.minDate !== undefined)?.minDate;
  const status = deriveMilestoneLifecycleStatus(milestone);

  return (
    <LifecycleTransitionDialog
      isOpen={isOpen}
      onClose={onClose}
      title={`Update ${milestone.name}`}
      currentStatusLabel={status}
      outcomes={outcomes}
      terminalCopy={
        outcomes.length === 0
          ? "This milestone is already paid — no further forward transitions available. Use the milestone edit form to correct any individual lifecycle date."
          : undefined
      }
      minDate={minDate}
      todayIso={getBrisbaneToday()}
      onSubmit={async (outcome, payload) => {
        const field = outcome.targetState as LifecycleField;
        const patch: MilestoneProgressPatch = { [field]: payload.date };
        if (payload.comment) {
          patch[COMMENT_FIELD[field]] = payload.comment;
        }
        await onProgress(milestone, patch);
      }}
      testIds={TEST_IDS}
    />
  );
}
